import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuditOpinionModified } from '../../../models/auditOpinionModified';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-audit-opinion-modified-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './audit-opinion-modified-list.component.html',
  styleUrl: './audit-opinion-modified-list.component.scss'
})
export class AuditOpinionModifiedListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  auditOpinionModifieds = signal<AuditOpinionModified[]>([]);
  rightItem: AuditOpinionModified | undefined = undefined;
  matchedItems: AuditOpinionModified[] | undefined = undefined;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadAuditOpinionModifieds();
  }

  loadAuditOpinionModifieds(): void {
    this.spinnerService.show();
    this.lutService.getAuditOpinionModifieds().subscribe({
      next: (data) => {
        const sortedData = this.sortAuditOpinionModifieds(data);
        this.auditOpinionModifieds.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading audit opinion modifieds:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortAuditOpinionModifieds(auditOpinionModifieds: AuditOpinionModified[]): AuditOpinionModified[] {
    return [...auditOpinionModifieds].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateAuditOpinionModified(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(auditOpinionModified: AuditOpinionModified): void {
  }

  onEditAuditOpinionModified(auditOpinionModified: AuditOpinionModified): void {
  }

  onDeleteAuditOpinionModified(auditOpinionModified: AuditOpinionModified): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(auditOpinionModified.name)
      .subscribe(result => {
        if (result) {
          const originalAuditOpinionModifieds = this.auditOpinionModifieds();

          this.auditOpinionModifieds.update(items => items.filter(aom => aom.id !== auditOpinionModified.id));

          this.spinnerService.show();
          this.lutService.deleteAuditOpinionModified(auditOpinionModified.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Audit opinion modified deleted successfully');
            },
            error: (error) => {
              console.error('Error deleting audit opinion modified:', error);
              this.spinnerService.hide();
              this.auditOpinionModifieds.set(originalAuditOpinionModifieds);
              this.toast.error(`<strong>Failed to delete Audit Opinion Modified:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: AuditOpinionModified, newName: string} | {item: AuditOpinionModified, newValue: string, columnKey: string}): void {
    const originalItem = { ...event.item };

    let updatedItem: AuditOpinionModified;
    if ('newName' in event) {
      updatedItem = { ...event.item, name: event.newName };
    } else {
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    this.auditOpinionModifieds.update(items => {
      const updated = items.map(aom => aom.id === event.item.id ? updatedItem : aom);
      return this.sortAuditOpinionModifieds(updated);
    });

    this.spinnerService.show();
    this.lutService.updateAuditOpinionModified(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit opinion modified updated successfully');
        this.auditOpinionModifieds.update(items => {
          const updated = items.map(aom => aom.id === event.item.id ? response : aom);
          return this.sortAuditOpinionModifieds(updated);
        });
      },
      error: (error) => {
        console.error('Error updating audit opinion modified:', error);
        this.spinnerService.hide();
        this.auditOpinionModifieds.update(items =>
          items.map(aom => aom.id === event.item.id ? originalItem : aom)
        );
        this.toast.error(`<strong>Failed to update Audit Opinion Modified:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    const tempId = crypto.randomUUID();
    const newAuditOpinionModified: AuditOpinionModified = { id: tempId, name: event['name'], systemReserved: false };

    this.auditOpinionModifieds.update(items => this.sortAuditOpinionModifieds([...items, newAuditOpinionModified]));

    this.spinnerService.show();
    this.lutService.createAuditOpinionModified({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit opinion modified created successfully');
        this.auditOpinionModifieds.update(items => {
          const updated = items.map(aom => aom.id === tempId ? response : aom);
          return this.sortAuditOpinionModifieds(updated);
        });
      },
      error: (error) => {
        console.error('Error creating audit opinion modified:', error);
        this.spinnerService.hide();
        this.auditOpinionModifieds.update(items =>
          items.filter(aom => aom.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Audit Opinion Modified:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: AuditOpinionModified, matchedItems?: AuditOpinionModified[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onAuditOpinionModifiedCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalAuditOpinionModifieds = this.auditOpinionModifieds();

        this.auditOpinionModifieds.update(items =>
          items.filter(aom => !bulkMergeRequest.leftIds.includes(aom.id))
        );

        this.rightItem = undefined;
        this.matchedItems = undefined;

        this.spinnerService.show();
        this.lutService.mergeAuditOpinionModifiedsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Audit opinion modifieds merged successfully');
            this.lookupTable.clearMatchingState();
            this.loadAuditOpinionModifieds();
          },
          error: (error) => {
            console.error('Error merging audit opinion modifieds:', error);
            this.spinnerService.hide();
            this.auditOpinionModifieds.set(originalAuditOpinionModifieds);
            this.rightItem = originalAuditOpinionModifieds.find(aom => aom.id === bulkMergeRequest.rightId);
            this.matchedItems = originalAuditOpinionModifieds.filter(aom => bulkMergeRequest.leftIds.includes(aom.id));
            this.toast.error(`<strong>Failed to merge Audit Opinion Modifieds:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
